// 「語音助理」帳號連結 (cs#14) — GET /v3/voice/links + DELETE /v3/voice/links/{provider}.
// Linking itself is initiated from the Google Home / Alexa app (browser OAuth →
// /v3/voice/oauth/*); this card shows link status, an unlink action, and a
// help link out for linking.
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';

interface VoiceLink {
  provider: string;
  state: string;
  linked_at?: number; // µs
}

const PROVIDERS = [
  { id: 'google', nameKey: 'voice.google', helpUrl: 'https://support.google.com/googlehome/answer/7073578' },
  { id: 'alexa', nameKey: 'voice.alexa', helpUrl: 'https://www.amazon.com/b?node=21576558011' },
] as const;

export default function VoiceAssistantsCard() {
  const { t } = useTranslation();
  const [links, setLinks] = useState<Record<string, VoiceLink> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await apiFetch<VoiceLink[]>('/v3/voice/links');
      const map: Record<string, VoiceLink> = {};
      (Array.isArray(res.data) ? res.data : []).forEach((l) => {
        map[l.provider] = l;
      });
      setLinks(map);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.loadFailedRetry'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const unlink = useCallback(
    async (provider: string, name: string) => {
      if (!window.confirm(t('voice.unlinkConfirm', { name }))) return;
      setBusyKey(provider);
      setActionError(null);
      try {
        await apiFetch(`/v3/voice/links/${provider}`, { method: 'DELETE', idempotencyKey: crypto.randomUUID() });
      } catch (e) {
        // 404 → already unlinked: idempotent success.
        if (!(e instanceof ApiError && e.status === 404)) {
          setActionError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.actionFailedRetry'));
        }
      } finally {
        setBusyKey(null);
        await load();
      }
    },
    [load, t]
  );

  return (
    <div className="card">
      <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('voice.title')}</h2>
      <p className="text-secondary">{t('voice.intro')}</p>

      {loading && <p className="text-secondary">{t('common.loading')}</p>}
      {error && <p className="error-text">{error}</p>}
      {actionError && <p className="error-text">{actionError}</p>}

      {!loading && !error && links && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          {PROVIDERS.map((p) => {
            const link = links[p.id];
            const name = t(p.nameKey);
            return (
              <div
                key={p.id}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-4)' }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{name}</div>
                  <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                    {link
                      ? t('voice.linkedSince', { date: link.linked_at ? formatTimestampUs(link.linked_at) : '' })
                      : t('voice.notLinked')}
                  </div>
                </div>
                {link ? (
                  <button className="btn-danger" disabled={busyKey !== null} onClick={() => void unlink(p.id, name)}>
                    {busyKey === p.id ? t('common.loading') : t('voice.unlink')}
                  </button>
                ) : (
                  <a className="btn-ghost" href={p.helpUrl} target="_blank" rel="noopener noreferrer">
                    {t('voice.howToLink')}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 0 }}>
        {t('voice.linkHint')}
      </p>
    </div>
  );
}
